
import React, { useEffect, useMemo, useState } from "react";
import bannerImg from "../assets/images/careerdetailban.jpg";
import dangerImg1 from "../assets/images/doc-detail/danger1.png";
import dangerImg2 from "../assets/images/doc-detail/danger2.png";
import dangerImg3 from "../assets/images/doc-detail/danger3.png";
import dangerImg4 from "../assets/images/doc-detail/danger4.png";

import {
  fetchRegulatoryComplianceCategories,
  fetchRegulatoryComplianceDetails,
  filterRegulatoryComplianceDetails,
  fetchBioMedicalWastePdfList,
  filterBioMedicalWastePdfList,
  fetchOrganTransplantReport,
  filterOrganTransplantReport,
  fetchRegulatoryComplianceBMW,
  fetchOrganTransplantCommitteeList,
} from "../Adapter/PageAdapter";

import { baseImage_path } from "../variables/Variables";

/**
 * RegulatoryCompliance.jsx (updated)
 * - Center-aligned tables
 * - BMW Annual Reports show 2 items by default + View More/Less
 * - Hash based tab navigation and auto-open on page load
 * - New tab: Organ Transplant – Committee Data (static table provided)
 * - Preserves existing classes/UI structure
 */

const DEFAULT_PAGE_SIZES = [10, 25, 50, "All"];

const PaginationMinimal = ({ total, page, setPage, pageSize, setPageSize }) => {
  const totalPages = pageSize === "All" ? 1 : Math.max(1, Math.ceil(total / pageSize));
  const pages = [];

  const makePages = () => {
    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
      return;
    }
    pages.push(1);
    const left = Math.max(2, page - 1);
    const right = Math.min(totalPages - 1, page + 1);

    if (left > 2) pages.push("dots-left");
    for (let i = left; i <= right; i++) pages.push(i);
    if (right < totalPages - 1) pages.push("dots-right");
    pages.push(totalPages);
  };

  makePages();

  const goPrev = () => setPage(Math.max(1, page - 1));
  const goNext = () => setPage(Math.min(totalPages, page + 1));

  return (
    <div className="rc-pagination" style={{ display: "flex", alignItems: "center", gap: 12, justifyContent: "flex-end", marginTop: 12 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <label style={{ fontSize: 14 }}>Rows:</label>
        <select
          value={pageSize}
          onChange={(e) => {
            const val = e.target.value === "All" ? "All" : Number(e.target.value);
            setPageSize(val);
            setPage(1);
          }}
          style={{ padding: 6 }}
        >
          {DEFAULT_PAGE_SIZES.map((s) => (
            <option value={s} key={s}>
              {s}
            </option>
          ))}
        </select>
      </div>

      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
        <button className="btn btn-light" onClick={goPrev} disabled={page <= 1}>
          Prev
        </button>

        {pages.map((p, i) =>
          p === "dots-left" || p === "dots-right" ? (
            <span key={p + i} style={{ padding: "6px 8px" }}>
              ...
            </span>
          ) : (
            <button
              key={p}
              onClick={() => setPage(p)}
              className={`btn ${p === page ? "btn-primary" : "btn-outline-secondary"}`}
              style={{ minWidth: 36 }}
            >
              {p}
            </button>
          )
        )}

        <button className="btn btn-light" onClick={goNext} disabled={page >= totalPages}>
          Next
        </button>
      </div>
    </div>
  );
};

const RegulatoryCompliance = () => {
  // categories
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);

  // BMW
  const [bmwList, setBmwList] = useState([]); // Stores ALL BMW data (default view)
  const [bmwFiltered, setBmwFiltered] = useState([]);
  const [bmwPdfList, setBmwPdfList] = useState([]); // Stores ALL BMW PDF data (default view)
  const [bmwPdfFiltered, setBmwPdfFiltered] = useState([]);
  const [bmwPdfShowAll, setBmwPdfShowAll] = useState(false);

  // Organ Transplant
  const [otList, setOtList] = useState([]); // Stores ALL OT data (default view)
  const [otFiltered, setOtFiltered] = useState([]);

  // Organ Transplant - Committee Data (managed from CMS)
  const [committeeData, setCommitteeData] = useState([]);

  // UI / filters
  const [activeTab, setActiveTab] = useState("docpills");
  const [selectedYear, setSelectedYear] = useState(""); // Default is unselected ("Select Year")
  const [selectedMonth, setSelectedMonth] = useState("");
  const [isMonthDisabled, setIsMonthDisabled] = useState(true);
  const [loading, setLoading] = useState(true);

  // pagination state per area
  const [bmwPage, setBmwPage] = useState(1);
  const [bmwPageSize, setBmwPageSize] = useState(10);

  const [pdfPage, setPdfPage] = useState(1);
  const [pdfPageSize, setPdfPageSize] = useState(10);

  const [otPage, setOtPage] = useState(1);
  const [otPageSize, setOtPageSize] = useState(10);

  const months = [
    { val: "1", label: "January" },
    { val: "2", label: "February" },
    { val: "3", label: "March" },
    { val: "4", label: "April" },
    { val: "5", label: "May" },
    { val: "6", label: "June" },
    { val: "7", label: "July" },
    { val: "8", label: "August" },
    { val: "9", label: "September" },
    { val: "10", label: "October" },
    { val: "11", label: "November" },
    { val: "12", label: "December" },
  ];

  // Static data for Knee Implants (Primary + Revision)
  const primaryKneeData = [
    { implant: "Primary Knee", component: "Femoral Component", material: "Titanium Alloy", priceWithoutGst: "₹ 42,614", priceWithGst: "₹ 44,744" },
    { implant: "Primary Knee", component: "Femoral Component", material: "Oxidized Zirconium", priceWithoutGst: "₹ 42,614", priceWithGst: "₹ 44,744" },
    { implant: "Primary Knee", component: "Femoral Component", material: "Hi-Flex", priceWithoutGst: "₹ 28,446", priceWithGst: "₹ 29,868" },
    { implant: "Primary Knee", component: "Femoral Component", material: "Cobalt Chromium", priceWithoutGst: "₹ 26,499", priceWithGst: "₹ 27,824" },
    { implant: "Primary Knee", component: "Tibial Component", material: "Titanium Alloy", priceWithoutGst: "₹ 26,708", priceWithGst: "₹ 28,043" },
    { implant: "Primary Knee", component: "Tibial Component", material: "Oxidized Zirconium", priceWithoutGst: "₹ 26,708", priceWithGst: "₹ 28,043" },
    { implant: "Primary Knee", component: "Tibial Component", material: "Cobalt Chromium", priceWithoutGst: "₹ 18,689", priceWithGst: "₹ 19,624" },
    { implant: "Primary Knee", component: "Articulating Surface", material: "Any Material", priceWithoutGst: "₹ 10,505", priceWithGst: "₹ 11,030" },
    { implant: "Primary Knee", component: "Patella", material: "Any Material", priceWithoutGst: "₹ 4,499", priceWithGst: "₹ 4,724" },
    { implant: "Primary Knee", component: "Tibial Tray + Insert", material: "Polyethylene or crosslinked polyethylene or high crosslinked polyethylene", priceWithoutGst: "₹ 14,256", priceWithGst: "₹ 14,968" },
    { implant: "Primary Knee", component: "Tibial Tray + Insert", material: "Tibial: Metallic Insert: Polyethylene or crosslinked polyethylene or high crosslinked polyethylene or any other material", priceWithoutGst: "₹ 29,200", priceWithGst: "₹ 30,660" },
  ];

  const revisionKneeData = [
    { implant: "Revision Knee", component: "Femoral Component", material: "Any Material", priceWithoutGst: "₹ 69,047", priceWithGst: "₹ 72,499" },
    { implant: "Revision Knee", component: "Tibial Component", material: "Any Material", priceWithoutGst: "₹ 34,342", priceWithGst: "₹ 36,059" },
    { implant: "Revision Knee", component: "Articulating Surface", material: "Any Material", priceWithoutGst: "₹ 17,457", priceWithGst: "₹ 18,330" },
    { implant: "Revision Knee", component: "Patella", material: "Any Material", priceWithoutGst: "₹ 4,499", priceWithGst: "₹ 4,724" },
  ];

  // Static data for Drug Eluting Stents (as provided)
  const drugElutingStents = [
    { brand: "XIENCE XPEDITION 48", manufacturer: "ABBOTT VASCULAR", price: "38,265.07", comments: "" },
    { brand: "XIENCE ALPINE", manufacturer: "ABBOTT VASCULAR", price: "38,265.07", comments: "" },
    { brand: "XIENCE PRIME", manufacturer: "ABBOTT VASCULAR", price: "22,500.00", comments: "" },
    { brand: "BIOMATRIX ALPHA", manufacturer: "BIOSENSOR", price: "38,265.07", comments: "" },
    { brand: "ORSIRO", manufacturer: "BIOTRONIK", price: "38,265.07", comments: "" },
    { brand: "SYNERGY", manufacturer: "BOSTON SCIENTIFIC", price: "38,265.07", comments: "" },
    { brand: "PROMUS ELITE", manufacturer: "BOSTON SCIENTIFIC", price: "22,500.00", comments: "" },
    { brand: "ABLUMINUS", manufacturer: "ENVISION SCEINTIFIC", price: "38,265.07", comments: "" },
    { brand: "MITIGATOR", manufacturer: "ENVISION SCEINTIFIC", price: "22,500.00", comments: "" },
    { brand: "RESOLUTE ONYX", manufacturer: "MEDTRONIC", price: "38,265.07", comments: "" },
    { brand: "RESOLUTE INTEGRITY", manufacturer: "MEDTRONIC", price: "22,500.00", comments: "" },
    { brand: "EVERMINE 50", manufacturer: "MERIL LIFE SCIENCES", price: "38,265.07", comments: "" },
    { brand: "BIOMIME MORPH", manufacturer: "MERIL LIFE SCIENCES", price: "38,265.07", comments: "" },
    { brand: "BIOMIME AURA", manufacturer: "MERIL LIFE SCIENCES", price: "38,265.07", comments: "" },
    { brand: "BIOMIME LINEAGE", manufacturer: "MERIL LIFE SCIENCES", price: "38,265.07", comments: "" },
    { brand: "BIOMIME", manufacturer: "MERIL LIFE SCIENCES", price: "22,500.00", comments: "" },
    { brand: "METAFOR", manufacturer: "MERIL LIFE SCIENCES", price: "22,500.00", comments: "" },
    { brand: "BIOMIME BRANCH", manufacturer: "MERIL LIFE SCIENCES", price: "38,265.07", comments: "" },
    { brand: "SUPRAFLEX CRUZ", manufacturer: "SMT", price: "38,265.07", comments: "" },
    { brand: "TETRIFLEX", manufacturer: "SMT", price: "38,265.07", comments: "" },
    { brand: "ULTIMASTER TENSEI", manufacturer: "TERUMO", price: "38,265.07", comments: "" },
    { brand: "ULTIMASTER", manufacturer: "TERUMO", price: "22,500.00", comments: "" },
    { brand: "YUKON CHOICE PC PLUS (DIS)", manufacturer: "TRANSLUMINA THERAPEUTICS", price: "38,265.07", comments: "" },
    { brand: "YUKON CHOICE PC ELITE", manufacturer: "TRANSLUMINA THERAPEUTICS", price: "38,265.07", comments: "" },
    { brand: "YUKON CHOICE VIVO ISAR", manufacturer: "TRANSLUMINA THERAPEUTICS", price: "38,265.07", comments: "" },
    { brand: "YUKON CHOICE ISAR SUMMIT", manufacturer: "TRANSLUMINA THERAPEUTICS", price: "38,265.07", comments: "" },
    { brand: "YUKON CHOICE FLEX", manufacturer: "TRANSLUMINA THERAPEUTICS", price: "22,500.00", comments: "" },
    { brand: "PRISTINE", manufacturer: "RELISYS", price: "38,265.07", comments: "" },
    { brand: "RELEASE-R", manufacturer: "RELISYS", price: "7,950.00", comments: "TN" },
    { brand: "REGIME", manufacturer: "ACE CARDIOPATHY", price: "22,500.00", comments: "" },
    { brand: "SAVIOUR", manufacturer: "ACE CARDIOPATHY", price: "38,265.07", comments: "" },
    { brand: "SIROLIMUS", manufacturer: "S3V VASCULAR TECHNOLOGIES", price: "30,811.00", comments: "Anandpur" },
    { brand: "EVERMAX", manufacturer: "NANO THERAPEUTICS", price: "8,100.00", comments: "TN" },
  ];

  // Bare Metal Stents static data (as provided)
  const bareMetalStents = [
    { brand: "GRAFTMASTER", manufacturer: "ABBOTT VASCULAR", price: "10,509.07" },
  ];

  // Helper to filter BMW data by category (local filtering)
  const applyCategoryFilter = (list, catId) => {
    if (!catId || !Array.isArray(list)) return list;
    return list.filter((r) => String(r.regulatory_compliance_category_id) === String(catId));
  };

  // Dynamically determine available years from the fetched BMW data
  const availableYears = useMemo(() => {
    if (!bmwList || bmwList.length === 0) return [];

    const years = bmwList.map(item => {
      if (item.date) {
        const parts = item.date.split('-');
        if (parts.length === 3) {
          // Check if first part is year (YYYY-MM-DD) or last part is year (DD-MM-YYYY)
          return parts[0].length === 4 ? parts[0] : parts[2];
        }
      }
      return null;
    }).filter(year => year && year.length === 4);

    return [...new Set(years)].sort((a, b) => b - a);
  }, [bmwList]);

  // Initial load: Fetches ALL data
  useEffect(() => {
    let mounted = true;
    const load = async () => {
      setLoading(true);
      try {
        const catRes = await fetchRegulatoryComplianceCategories();
        const cats = catRes?.data || [];
        if (!mounted) return;
        setCategories(cats);
        setSelectedCategory(cats[0]?.id || null);

        const bmwRes = await fetchRegulatoryComplianceBMW();
        const bmwData = bmwRes?.data || [];
        if (!mounted) return;
        setBmwList(bmwData);
        setBmwFiltered(bmwData);

        const pdfRes = await fetchBioMedicalWastePdfList();
        const pdfData = pdfRes?.data || [];
        if (!mounted) return;
        setBmwPdfList(pdfData);
        setBmwPdfFiltered(pdfData);

        const otRes = await fetchOrganTransplantReport();
        const otData = otRes?.data || [];
        if (!mounted) return;
        setOtList(otData);
        setOtFiltered(otData);

        // On initial load, if URL hash present - open that tab
        // const hash = (window.location.hash || "").replace("#", "");
        // if (hash) {
        //   // map possible hash values to tab ids
        //   const map = {
        //     "statutory-monthly-report": "docpills",
        //     "stent-pricing": "nursepills",
        //     "ortho-knee-implant-pricing": "kneeplant",
        //     "organ-transplant-donation-data": "organplant",
        //     "organ-transplant-committe-data": "committeeplant",
        //   };
        //   if (map[hash]) setActiveTab(map[hash]);
        // }
      } catch (err) {
        console.error("Initial load error:", err);
      } finally {
        if (mounted) setLoading(false);
      }
    };
    load();
    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Committee Data is loaded separately so a failure here doesn't block the other tabs
  useEffect(() => {
    let mounted = true;
    fetchOrganTransplantCommitteeList()
      .then((res) => {
        if (mounted) setCommitteeData(res?.data || []);
      })
      .catch((err) => console.error("Committee data load error:", err));
    return () => {
      mounted = false;
    };
  }, []);

  // API returns YYYY-MM-DD; the table shows DD-MM-YYYY
  const formatCommitteeDate = (value) => {
    if (!value) return "-";
    const [year, month, day] = String(value).slice(0, 10).split("-");
    return day && month && year ? `${day}-${month}-${year}` : value;
  };

  useEffect(() => {
    const handleHashChange = () => {
      const hash = (window.location.hash || "").replace("#", "");

      const map = {
        "statutory-monthly-report": "docpills",
        "stent-pricing": "nursepills",
        "ortho-knee-implant-pricing": "kneeplant",
        "organ-transplant-donation-data": "organplant",
        "organ-transplant-committee-data": "committeeplant",
      };

      if (map[hash]) {
        setActiveTab(map[hash]);

        // scroll into view after tab change
        setTimeout(() => {
          const section = document.getElementById("regulatory-tabs-section");
          if (section) {
            const yOffset = -120;
            const y = section.getBoundingClientRect().top + window.pageYOffset + yOffset;
            window.scrollTo({ top: y, behavior: "smooth" });
          }
        }, 200);
      }
    };

    // Run once on load
    handleHashChange();

    // Run whenever hash changes
    window.addEventListener("hashchange", handleHashChange);

    return () => {
      window.removeEventListener("hashchange", handleHashChange);
    };
  }, []);


  const applyBMWServerFilter = async (year, month, categoryId) => {
    if (!year) {
      setBmwFiltered(applyCategoryFilter(bmwList, categoryId));
      setBmwPdfFiltered(bmwPdfList);
      return;
    }

    setLoading(true);
    try {
      const tableRes = await filterRegulatoryComplianceDetails(
        Number(year),
        month ? Number(month) : null
      );

      const tableData = tableRes?.data || [];
      const tableFiltered = applyCategoryFilter(tableData, categoryId);
      setBmwFiltered(tableFiltered);

      const pdfRes = await filterBioMedicalWastePdfList({
        year: Number(year)
      });

      setBmwPdfFiltered(pdfRes?.data || []);

    } catch (err) {
      console.error("BMW filter error:", err);
    } finally {
      setLoading(false);
      setBmwPage(1);
      setPdfPage(1);
    }
  };

  const applyOTServerFilter = async (year, month) => {
    if (!year) {
      setOtFiltered(otList);
      return;
    }
    setLoading(true);
    try {
      const res = await filterOrganTransplantReport(Number(year), month ? Number(month) : null);
      setOtFiltered(res?.data || []);
    } catch (err) {
      console.error("OT filter error:", err);
    } finally {
      setLoading(false);
      setOtPage(1);
    }
  };

  // handlers
  const handleCategoryClick = (catId) => {
    setSelectedCategory(catId);

    if (selectedYear) {
      applyBMWServerFilter(selectedYear, selectedMonth, catId);
    }
    else {
      setBmwFiltered(applyCategoryFilter(bmwList, catId));
    }
    setBmwPage(1);
  };

  const handleYearChange = (e) => {
    const year = e.target.value;
    setSelectedYear(year);
    setSelectedMonth("");

    if (year) {
      setIsMonthDisabled(false);
      if (activeTab === "docpills") applyBMWServerFilter(year, "", selectedCategory);
      if (activeTab === "organplant") applyOTServerFilter(year, "");
    } else {
      setIsMonthDisabled(true);
      if (activeTab === "docpills") {
        setBmwFiltered(applyCategoryFilter(bmwList, selectedCategory));
        setBmwPdfFiltered(bmwPdfList);
      }
      if (activeTab === "organplant") setOtFiltered(otList);
    }
  };

  const handleMonthChange = (e) => {
    const month = e.target.value;
    setSelectedMonth(month);

    if (!selectedYear) return;
    if (activeTab === "docpills") applyBMWServerFilter(selectedYear, month, selectedCategory);
    if (activeTab === "organplant") applyOTServerFilter(selectedYear, month);
  };

  const changeTab = (tabId) => {
    setActiveTab(tabId);

    // update URL hash mapping
    const mapReverse = {
      docpills: "statutory-monthly-report",
      nursepills: "stent-pricing",
      kneeplant: "ortho-knee-implant-pricing",
      organplant: "organ-transplant-donation-data",
      committeeplant: "organ-transplant-committee-data",
    };
    const hash = mapReverse[tabId] || "";
    if (hash) {
      try {
        window.history.replaceState(null, "", window.location.pathname + "#" + hash);
      } catch (e) {
        // ignore
      }
    }

    if (tabId === "docpills") {
      if (selectedYear) applyBMWServerFilter(selectedYear, selectedMonth, selectedCategory);
      else setBmwFiltered(applyCategoryFilter(bmwList, selectedCategory));
      setBmwPage(1);
    } else if (tabId === "organplant") {
      if (selectedYear) applyOTServerFilter(selectedYear, selectedMonth);
      else setOtFiltered(otList);
      setOtPage(1);
    }
  };

  const paginate = (arr, page, pageSize) => {
    if (!Array.isArray(arr)) return [];
    if (pageSize === "All") return arr;
    const size = Number(pageSize);
    const start = (page - 1) * size;
    return arr.slice(start, start + size);
  };

  const displayedBmw = useMemo(() => paginate(bmwFiltered, bmwPage, bmwPageSize), [bmwFiltered, bmwPage, bmwPageSize]);
  const displayedPdf = useMemo(() => paginate(bmwPdfFiltered, pdfPage, pdfPageSize), [bmwPdfFiltered, pdfPage, pdfPageSize]);
  const displayedOt = useMemo(() => paginate(otFiltered, otPage, otPageSize), [otFiltered, otPage, otPageSize]);

  // shared style for centered cells
  const thStyle = { textAlign: "center", padding: "8px" };
  const tdStyle = { textAlign: "center", padding: "8px" };

  const renderBMWPdfList = () => {
    if (!bmwPdfFiltered || bmwPdfFiltered.length === 0) {
      return <p className="mb-3">No PDF reports available for the selected filter(s).</p>;
    }

    const showCount = bmwPdfShowAll ? bmwPdfFiltered.length : Math.min(2, bmwPdfFiltered.length);
    const listToShow = bmwPdfFiltered.slice(0, showCount);

    return (
      <div className="pdf-list-section mb-3">
        <div className="cust-container">
          <h4>Annual Reports</h4>
          <ul>
            {listToShow.map((file, idx) => (
              <li key={idx}>
                <a
                  href={file.pdf_file ? `${baseImage_path}${file.pdf_file}` : "#"}
                  target="_blank"
                  rel="noreferrer"
                >
                  {file.name || `File ${idx + 1}`}
                </a>
              </li>
            ))}
          </ul>

          {bmwPdfFiltered.length > 2 && (
            <div style={{ marginTop: 8 }}>
              <button
                className="btn btn-outline-primary"
                onClick={() => setBmwPdfShowAll(!bmwPdfShowAll)}
              >
                {bmwPdfShowAll ? "View Less" : `View More (${bmwPdfFiltered.length - 2} more)`}
              </button>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderBMWTable = () => {
    const hasData = displayedBmw && displayedBmw.length > 0;

    return (
      <>
        <div className="reg-table-wrapper" style={{ overflowX: "auto" }}>
          <table className="reg-table" style={{ width: "100%", borderCollapse: "collapse", minWidth: 900 }}>
            <thead>
              <tr>
                <th rowSpan="2" style={thStyle}>S. No</th>
                <th rowSpan="2" style={thStyle}>Date</th>
                <th colSpan="2" style={thStyle}>Blue Category</th>
                <th colSpan="2" style={thStyle}>Red Category</th>
                <th colSpan="2" style={thStyle}>White Category</th>
                <th colSpan="2" style={thStyle}>Yellow Category</th>
                <th colSpan="2" style={thStyle}>Cytotoxic</th>
                <th colSpan="2" style={thStyle}>COVID Waste</th>
                <th colSpan="2" style={thStyle}>Sharp Container</th>
              </tr>
              <tr>
                <th style={thStyle}>Bags</th>
                <th style={thStyle}>WT KG</th>
                <th style={thStyle}>Bags</th>
                <th style={thStyle}>WT KG</th>
                <th style={thStyle}>Bags</th>
                <th style={thStyle}>WT KG</th>
                <th style={thStyle}>Bags</th>
                <th style={thStyle}>WT KG</th>
                <th style={thStyle}>Bags</th>
                <th style={thStyle}>WT KG</th>
                <th style={thStyle}>Bags</th>
                <th style={thStyle}>WT KG</th>
                <th style={thStyle}>Bags</th>
                <th style={thStyle}>WT KG</th>
              </tr>
            </thead>

            <tbody>
              {!hasData ? (
                <tr>
                  <td colSpan="16" style={{ textAlign: 'center', padding: '20px' }}>
                    No Bio-Medical Waste data for the selected filters.
                  </td>
                </tr>
              ) : (
                displayedBmw.map((row, idx) => (
                  <tr key={idx}>
                    <td style={tdStyle}>
                      {(bmwPage - 1) * (bmwPageSize === "All" ? bmwFiltered.length : bmwPageSize) + idx + 1}
                    </td>
                    <td style={tdStyle}>
                      {(() => {
                        const rawDate = row.date;
                        if (rawDate) {
                          const parts = rawDate.split('-');
                          if (parts.length === 3) {
                            let day, monthIndex, year;
                            if (parts[0].length === 4) {
                              // YYYY-MM-DD
                              year = parseInt(parts[0], 10);
                              monthIndex = parseInt(parts[1], 10) - 1;
                              day = parseInt(parts[2], 10);
                            } else {
                              // DD-MM-YYYY
                              day = parseInt(parts[0], 10);
                              monthIndex = parseInt(parts[1], 10) - 1;
                              year = parseInt(parts[2], 10);
                            }

                            const date = new Date(year, monthIndex, day);

                            if (!isNaN(date.getTime())) {
                              return new Intl.DateTimeFormat('en-IN', { year: 'numeric', month: 'long' }).format(date);
                            }
                          }
                        }
                        return row.month_name || rawDate || "-";
                      })()}
                    </td>
                    <td style={tdStyle}>{row.blue_bags ?? "-"}</td>
                    <td style={tdStyle}>{row.blue_weight ?? "-"}</td>
                    <td style={tdStyle}>{row.red_bags ?? "-"}</td>
                    <td style={tdStyle}>{row.red_weight ?? "-"}</td>
                    <td style={tdStyle}>{row.white_bags ?? "-"}</td>
                    <td style={tdStyle}>{row.white_weight ?? "-"}</td>
                    <td style={tdStyle}>{row.yellow_bags ?? "-"}</td>
                    <td style={tdStyle}>{row.yellow_weight ?? "-"}</td>
                    <td style={tdStyle}>{row.cytotoxic_yellow_bags ?? "-"}</td>
                    <td style={tdStyle}>{row.cytotoxic_yellow_weight ?? "-"}</td>
                    <td style={tdStyle}>{row.covid_bags ?? "-"}</td>
                    <td style={tdStyle}>{row.covid_weight ?? "-"}</td>
                    <td style={tdStyle}>{row.sharp_bags ?? "-"}</td>
                    <td style={tdStyle}>{row.sharp_weight ?? "-"}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {hasData && (
          <PaginationMinimal
            total={bmwFiltered.length}
            page={bmwPage}
            setPage={setBmwPage}
            pageSize={bmwPageSize}
            setPageSize={(v) => {
              setBmwPageSize(v);
              setBmwPage(1);
            }}
          />
        )}
      </>
    );
  };

  const renderOTTable = () => {
    if (!otFiltered || otFiltered.length === 0) {
      return <p>No Organ Transplant records for the selected filters.</p>;
    }

    return (
      <>
        <div className="reg-table-wrapper" style={{ overflowX: "auto" }}>
          <table className="reg-table" style={{ width: "100%", borderCollapse: "collapse", minWidth: 800 }}>
            <thead>
              <tr>
                <th style={thStyle}>Hospital</th>
                <th style={thStyle}>Date</th>
                <th style={thStyle}>UHID of the Recipient</th>
                <th style={thStyle}>Organ</th>
                <th style={thStyle}>Donor Type</th>
                <th style={thStyle}>Results</th>
              </tr>
            </thead>
            <tbody>
              {displayedOt.map((row, idx) => (
                <tr key={idx}>
                  <td style={tdStyle}>S L Raheja Hospital, Mumbai</td>
                  <td style={tdStyle}>{row.date ?? "-"}</td>
                  <td style={tdStyle}>{row.uhid_recipient ?? "-"}</td>
                  <td style={tdStyle}>{row.organ ?? "-"}</td>
                  <td style={tdStyle}>{row.donor_type ?? "-"}</td>
                  <td style={tdStyle}>{row.results ?? "-"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <PaginationMinimal
          total={otFiltered.length}
          page={otPage}
          setPage={setOtPage}
          pageSize={otPageSize}
          setPageSize={(v) => {
            setOtPageSize(v);
            setOtPage(1);
          }}
        />
      </>
    );
  };

  const renderCommitteeTable = () => {
    if (!committeeData || committeeData.length === 0) {
      return <p>No Committee data available.</p>;
    }

    return (
      <div className="reg-table-wrapper" style={{ overflowX: "auto" }}>
        <table className="reg-table" style={{ width: "100%", borderCollapse: "collapse", minWidth: 900 }}>
          <thead>
            <tr>
              <th style={thStyle}>Hospital</th>
              <th style={thStyle}>Date</th>
              <th style={thStyle}>Type of Committee</th>
              <th style={thStyle}>Code No Assigned to the Case</th>
              <th style={thStyle}>Results</th>
            </tr>
          </thead>
          <tbody>
            {committeeData.map((row, idx) => (
              <tr key={row.id ?? idx}>
                <td style={tdStyle}>{row.hospital || "S L Raheja Hospital, Mumbai"}</td>
                <td style={tdStyle}>{formatCommitteeDate(row.date)}</td>
                <td style={tdStyle}>{row.committee_type ?? "-"}</td>
                <td style={tdStyle}>{row.code_no ?? "-"}</td>
                <td style={tdStyle}>{row.results ?? "-"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  if (loading) return <div>Loading...</div>;

  return (
    <>
      {/* Banner Section (unchanged classes) */}
      <section className="banner-spac">
        <div className="cust-container">
          <div className="banner-box">
            <img src={bannerImg} alt="Banner" />
            <div className="ban-con">
              <h1>Regulatory Compliance</h1>
            </div>
          </div>
        </div>
      </section>

      {/* Main Section (keep classes) */}
      <section id="regulatory-tabs-section" className="academics-docnurse-sec mrgntop70 margnbot90">
        <div className="cust-container">
          <div className="academics-docnurse-inner">
            <div className="academics-docnurse-tabs regulcompliance">

              {/* MAIN nav-pills - kept original structure & classes */}
              <ul className="nav nav-pills mb-3 justify-content-center academic-ul" id="pills-tab" role="tablist">
                <li className="nav-item" role="presentation">
                  <button
                    className={`nav-link doccls ${activeTab === "docpills" ? "active" : ""}`}
                    type="button"
                    onClick={() => changeTab("docpills")}
                  >
                    <div className="academics-docnurse-tabs-icn">
                      <img src={dangerImg1} alt="Bio-Medical Waste" />
                    </div>
                    <span>Bio-Medical Waste Management</span>
                  </button>
                </li>

                <li className="nav-item" role="presentation">
                  <button
                    className={`nav-link nursecls ${activeTab === "nursepills" ? "active" : ""}`}
                    type="button"
                    onClick={() => changeTab("nursepills")}
                  >
                    <div className="academics-docnurse-tabs-icn">
                      <img src={dangerImg2} alt="Stent Charges" />
                    </div>
                    <span>Stent Charges</span>
                  </button>
                </li>

                <li className="nav-item" role="presentation">
                  <button
                    className={`nav-link nursecls ${activeTab === "kneeplant" ? "active" : ""}`}
                    type="button"
                    onClick={() => changeTab("kneeplant")}
                  >
                    <div className="academics-docnurse-tabs-icn">
                      <img src={dangerImg3} alt="Knee Implants" />
                    </div>
                    <span>Knee Implants - Charges</span>
                  </button>
                </li>

                <li className="nav-item" role="presentation">
                  <button
                    className={`nav-link nursecls ${activeTab === "organplant" ? "active" : ""}`}
                    type="button"
                    onClick={() => changeTab("organplant")}
                  >
                    <div className="academics-docnurse-tabs-icn">
                      <img src={dangerImg4} alt="Organ Transplant" />
                    </div>
                    <span>Organ Transplant - Donation Data</span>
                  </button>
                </li>

                <li className="nav-item" role="presentation">
                  <button
                    className={`nav-link nursecls ${activeTab === "committeeplant" ? "active" : ""}`}
                    type="button"
                    onClick={() => changeTab("committeeplant")}
                  >
                    <div className="academics-docnurse-tabs-icn">
                      <img src={dangerImg4} alt="Committee Data" />
                    </div>
                    <span>Organ Transplant - Committee Data</span>
                  </button>
                </li>
              </ul>

              {/* Filters: Year + Month (kept markup classes)
                  Hidden for Stent (nursepills), Knee Implant (kneeplant), and Committee tabs */}
              {activeTab !== "nursepills" && activeTab !== "kneeplant" && activeTab !== "committeeplant" && (
                <div className="select-yearandmonthtab">
                  <div className="yearbox">
                    <select value={selectedYear} onChange={handleYearChange}>
                      <option value="">Select Year</option>
                      {availableYears.map(year => (
                        <option value={year} key={year}>
                          {year}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="yearbox">
                    <select value={selectedMonth} onChange={handleMonthChange} disabled={isMonthDisabled}>
                      <option value="">Select Month</option>
                      {months.map((m) => (
                        <option value={m.val} key={m.val}>
                          {m.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              )}

              {/* TAB CONTENT area */}
              <div className="tab-content regulartable">
                {activeTab === "docpills" && (
                  <div className="doc-nurse-tabpanel fade show active">
                    {/* PDFs first */}
                    {renderBMWPdfList()}

                    {/* Table next */}
                    {renderBMWTable()}
                  </div>
                )}

                {activeTab === "nursepills" && (
                  <div className="doc-nurse-tabpanel fade show active">

                    <h4 className="mb-4 mt-4">Drug Eluting Stents</h4>

                    <div className="reg-table-wrapper" style={{ overflowX: "auto" }}>
                      <table className="reg-table" style={{ width: "100%", borderCollapse: "collapse" }}>
                        <thead>
                          <tr>
                            <th style={thStyle}>Brand</th>
                            <th style={thStyle}>Manufacturer</th>
                            <th style={thStyle}>Ceiling Price With GST</th>
                            <th style={thStyle}>Comments</th>
                          </tr>
                        </thead>

                        <tbody>
                          {drugElutingStents.map((stent, idx) => (
                            <tr key={idx}>
                              <td style={tdStyle}>{stent.brand}</td>
                              <td style={tdStyle}>{stent.manufacturer}</td>
                              <td style={tdStyle}>{stent.price}</td>
                              <td style={tdStyle}>{stent.comments || "-"}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    <h4 className="mb-3 mt-4">Bare Metal Stents</h4>

                    <div className="reg-table-wrapper" style={{ overflowX: "auto" }}>
                      <table className="reg-table">
                        <thead>
                          <tr>
                            <th style={thStyle}>Brand</th>
                            <th style={thStyle}>Manufacturer</th>
                            <th style={thStyle}>Ceiling Price With GST</th>
                          </tr>
                        </thead>
                        <tbody>
                          {bareMetalStents.map((b, i) => (
                            <tr key={i}>
                              <td style={tdStyle}>{b.brand}</td>
                              <td style={tdStyle}>{b.manufacturer}</td>
                              <td style={tdStyle}>{b.price}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                  </div>
                )}

                {activeTab === "kneeplant" && (
                  <div className="doc-nurse-tabpanel fade show active">

                    <h4 className="mb-4 mt-4">Ceiling Prices on Primary Knee Implant</h4>

                    <div className="reg-table-wrapper" style={{ overflowX: "auto" }}>
                      <table className="reg-table">
                        <thead>
                          <tr>
                            <th style={thStyle}>Ortho Knee Implant</th>
                            <th style={thStyle}>Component</th>
                            <th style={thStyle}>Material</th>
                            <th style={thStyle}>Ceiling Price without GST</th>
                            <th style={thStyle}>Ceiling Price with GST (5%)</th>
                          </tr>
                        </thead>

                        <tbody>
                          {primaryKneeData.map((row, idx) => (
                            <tr key={idx}>
                              <td style={tdStyle}>{row.implant}</td>
                              <td style={tdStyle}>{row.component}</td>
                              <td style={tdStyle}>{row.material}</td>
                              <td style={tdStyle}>{row.priceWithoutGst}</td>
                              <td style={tdStyle}>{row.priceWithGst}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    <h4 className="mb-3 mt-4">Ceiling Prices on Revision Knee Implant</h4>

                    <div className="reg-table-wrapper" style={{ overflowX: "auto" }}>
                      <table className="reg-table">
                        <thead>
                          <tr>
                            <th style={thStyle}>Ortho Knee Implant</th>
                            <th style={thStyle}>Component</th>
                            <th style={thStyle}>Material</th>
                            <th style={thStyle}>Ceiling Price without GST</th>
                            <th style={thStyle}>Ceiling Price with GST (5%)</th>
                          </tr>
                        </thead>

                        <tbody>
                          {revisionKneeData.map((row, idx) => (
                            <tr key={idx}>
                              <td style={tdStyle}>{row.implant}</td>
                              <td style={tdStyle}>{row.component}</td>
                              <td style={tdStyle}>{row.material}</td>
                              <td style={tdStyle}>{row.priceWithoutGst}</td>
                              <td style={tdStyle}>{row.priceWithGst}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                  </div>
                )}

                {activeTab === "organplant" && (
                  <div className="doc-nurse-tabpanel fade show active">
                    {renderOTTable()}
                  </div>
                )}

                {activeTab === "committeeplant" && (
                  <div className="doc-nurse-tabpanel fade show active">
                    <h4 className="mb-4 mt-4">Organ Transplant - Committee Data</h4>
                    {renderCommitteeTable()}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default RegulatoryCompliance;